import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Autocomplete, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { DOMAIN_API_HOST } from "../../../configs/main";
import useAdminUser from "../../../data/useAdminUser";
import { citiesData } from "../Ssp/geolist";
import "./SiteVisits.scss";

const ALLOWED_EMAILS = ["e.usikova@programmatic.ru", "a.tuhtaev@programmatic.ru"];

const STATUS_LABEL = {
  queued: "В очереди",
  running: "Идёт",
  done: "Готово",
  failed: "Ошибка",
  error: "Ошибка",
  cancelled: "Отменено",
};

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: "Bearer " + localStorage.getItem("admin_token"),
  };
}

function errorDetail(data, fallback) {
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  return fallback;
}

const RU_TO_LAT = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z",
  и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r",
  с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch",
  ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

const REGION_RU = {
  "Adygeya Republic": "Республика Адыгея",
  "Altai": "Республика Алтай",
  "Altai Krai": "Алтайский край",
  "Amur Oblast": "Амурская область",
  "Arkhangelskaya": "Архангельская область",
  "Astrakhan Oblast": "Астраханская область",
  "Bashkortostan Republic": "Республика Башкортостан",
  "Belgorod Oblast": "Белгородская область",
  "Bryansk Oblast": "Брянская область",
  "Buryatiya Republic": "Республика Бурятия",
  "Chechnya": "Чеченская Республика",
  "Chelyabinsk Oblast": "Челябинская область",
  "Chuvash Republic": "Чувашская Республика",
  "Dagestan": "Республика Дагестан",
  "Ingushetiya Republic": "Республика Ингушетия",
  "Irkutsk Oblast": "Иркутская область",
  "Ivanovo Oblast": "Ивановская область",
  "Kabardino-Balkariya Republic": "Кабардино-Балкарская Республика",
  "Kaliningrad Oblast": "Калининградская область",
  "Kalmykiya Republic": "Республика Калмыкия",
  "Kaluga Oblast": "Калужская область",
  "Kamchatka": "Камчатский край",
  "Karachayevo-Cherkesiya Republic": "Карачаево-Черкесская Республика",
  "Karelia": "Республика Карелия",
  "Kemerovo Oblast": "Кемеровская область",
  "Khabarovsk": "Хабаровский край",
  "Khakasiya Republic": "Республика Хакасия",
  "Khanty-Mansia": "Ханты-Мансийский автономный округ",
  "Kirov Oblast": "Кировская область",
  "Komi": "Республика Коми",
  "Kostroma Oblast": "Костромская область",
  "Krasnodar Krai": "Краснодарский край",
  "Krasnoyarsk Krai": "Красноярский край",
  "Kurgan Oblast": "Курганская область",
  "Kursk Oblast": "Курская область",
  "Leningrad Oblast": "Ленинградская область",
  "Lipetsk Oblast": "Липецкая область",
  "Magadan Oblast": "Магаданская область",
  "Mariy-El Republic": "Республика Марий Эл",
  "Mordoviya Republic": "Республика Мордовия",
  "Moscow": "Москва",
  "Moscow Oblast": "Московская область",
  "Murmansk": "Мурманская область",
  "Nenets": "Ненецкий автономный округ",
  "Nizhny Novgorod Oblast": "Нижегородская область",
  "North Ossetia–Alania": "Республика Северная Осетия — Алания",
  "Novgorod Oblast": "Новгородская область",
  "Novosibirsk Oblast": "Новосибирская область",
  "Omsk Oblast": "Омская область",
  "Orenburg Oblast": "Оренбургская область",
  "Oryol oblast": "Орловская область",
  "Penza Oblast": "Пензенская область",
  "Perm Krai": "Пермский край",
  "Primorye": "Приморский край",
  "Pskov Oblast": "Псковская область",
  "Republic of Tyva": "Республика Тыва",
  "Rostov Oblast": "Ростовская область",
  "Ryazan Oblast": "Рязанская область",
  "Sakha": "Республика Саха (Якутия)",
  "Sakhalin Oblast": "Сахалинская область",
  "Samara Oblast": "Самарская область",
  "Saratov Oblast": "Саратовская область",
  "Smolensk Oblast": "Смоленская область",
  "St.-Petersburg": "Санкт-Петербург",
  "Stavropol Kray": "Ставропольский край",
  "Sverdlovsk Oblast": "Свердловская область",
  "Tambov Oblast": "Тамбовская область",
  "Tatarstan Republic": "Республика Татарстан",
  "Tomsk Oblast": "Томская область",
  "Transbaikal Territory": "Забайкальский край",
  "Tula Oblast": "Тульская область",
  "Tver Oblast": "Тверская область",
  "Tyumen Oblast": "Тюменская область",
  "Udmurtiya Republic": "Удмуртская Республика",
  "Ulyanovsk Oblast": "Ульяновская область",
  "Vladimir Oblast": "Владимирская область",
  "Volgograd Oblast": "Волгоградская область",
  "Vologda Oblast": "Вологодская область",
  "Voronezh Oblast": "Воронежская область",
  "Yamalo-Nenets": "Ямало-Ненецкий автономный округ",
  "Yaroslavl Oblast": "Ярославская область",
  "Yevrey (Jewish) Autonomous Oblast": "Еврейская автономная область",
};

const LEGACY_RU = new Set([
  "ru", "moscow_area", "moscow_oblast", "moscow", "petersburg", "spb", "novgorod",
  "ivanovo", "kaliningrad", "nizhny", "novosibirsk", "irkutsk", "kirov",
]);

const GEO_SPECIALS = [
  { id: "ru", label: "Вся Россия", search: "вся россия russia рф ru" },
  { id: "moscow_area", label: "Москва + область", search: "москва область подмосковье moscow oblast московская" },
];

const REGION_SEARCH = {
  "Moscow Oblast": "московская область москва подмосковье moscow oblast",
  "Moscow": "москва регион зеленоград",
  "St.-Petersburg": "санкт-петербург петербург питер спб",
  "Leningrad Oblast": "ленинградская область ло",
};

function foldQuery(value) {
  return value
    .toLowerCase()
    .split("")
    .map((char) => RU_TO_LAT[char] ?? char)
    .join("");
}

function ruWord(word) {
  const key = word.toLowerCase();
  const tokens = [
    ["shch", "щ"], ["sch", "щ"], ["tsky", "тский"], ["tsk", "тск"], ["sky", "ский"],
    ["zh", "ж"], ["kh", "х"], ["ch", "ч"], ["sh", "ш"],
    ["yu", "ю"], ["ya", "я"], ["ye", "е"], ["yo", "ё"], ["iy", "ий"],
  ];
  const letters = {
    a: "а", b: "б", c: "к", d: "д", e: "е", f: "ф", g: "г", h: "х", i: "и",
    j: "й", k: "к", l: "л", m: "м", n: "н", o: "о", p: "п", r: "р", s: "с",
    t: "т", u: "у", v: "в", w: "в", x: "кс", y: "ы", z: "з", "'": "ь",
  };
  let out = "";
  let i = 0;
  while (i < key.length) {
    const token = tokens.find(([src]) => key.startsWith(src, i));
    if (token) {
      out += token[1];
      i += token[0].length;
      continue;
    }
    out += letters[key[i]] || key[i];
    i += 1;
  }
  return out.charAt(0).toUpperCase() + out.slice(1);
}

function ruPlace(name) {
  if (!name) return "";
  if (citiesData[name]) return citiesData[name];
  return name.split(/(\s+|-)/).map((part) => (part && /[A-Za-z]/.test(part[0]) ? ruWord(part) : part)).join("");
}

export function isRussianCity(item) {
  const id = item?.id || "";
  return id.startsWith("RU|") || LEGACY_RU.has(id);
}

export function isRegionGeo(item) {
  const id = item?.id || "";
  if (id === "moscow_oblast" || id === "moscow_area") return true;
  const parts = id.split("|");
  return parts[0] === "RU" && Boolean(parts[1]) && !parts[2];
}

export function geoGroup(item) {
  const id = item?.id || "";
  if (id === "ru") return "Страна";
  if (isRegionGeo(item)) return "Области и регионы";
  return "Города";
}

export function cityTitle(item) {
  const id = item?.id || "";
  const label = item?.label || "";
  if (id === "ru") return label || "Вся Россия";
  if (id === "moscow_area") return label || "Москва + область";
  if (id === "moscow_oblast") return label || "Московская область";
  if (isRegionGeo(item)) return REGION_RU[label] || REGION_RU[id.split("|")[1]] || ruPlace(label);
  if (!label || !isRussianCity(item)) return label;
  const comma = label.indexOf(", ");
  const city = comma === -1 ? label : label.slice(0, comma);
  const region = comma === -1 ? "" : label.slice(comma + 2);
  const cityRu = ruPlace(city);
  if (!region) return cityRu;
  return `${cityRu}, ${REGION_RU[region] || ruPlace(region)}`;
}

function geoSearchText(item) {
  const shown = cityTitle(item);
  const regionKey = isRegionGeo(item) ? (item.label || item.id.split("|")[1] || "") : "";
  return [
    item.search,
    item.label,
    item.id,
    shown,
    REGION_RU[regionKey],
    REGION_SEARCH[regionKey],
    foldQuery(shown),
    foldQuery(item.label || ""),
  ].filter(Boolean).join(" ").toLowerCase();
}

export function mergeGeoOptions(remote) {
  const byId = new Map();
  const extras = Object.entries(REGION_RU).map(([en, ru]) => ({
    id: `RU|${en}|`,
    label: en,
    search: `${en} ${ru} ${REGION_SEARCH[en] || ""}`.trim(),
  }));
  [...GEO_SPECIALS, ...extras, ...(remote || [])].forEach((item) => {
    if (!item?.id || !isRussianCity(item)) return;
    if (!byId.has(item.id)) byId.set(item.id, { ...item });
  });
  const specials = GEO_SPECIALS.map((item) => byId.get(item.id)).filter(Boolean);
  const regions = [...byId.values()]
    .filter((item) => isRegionGeo(item) && item.id !== "moscow_area")
    .sort((a, b) => cityTitle(a).localeCompare(cityTitle(b), "ru"));
  const cities = [...byId.values()].filter((item) => item.id !== "ru" && !isRegionGeo(item));
  return [...specials, ...regions, ...cities].map((item) => ({
    ...item,
    search: geoSearchText(item),
  }));
}

export function filterCities(options, state) {
  const raw = state.inputValue.trim().toLowerCase();
  if (!raw) return options;
  const folded = foldQuery(raw);
  const matched = options.filter((item) => {
    const hay = geoSearchText(item);
    return hay.includes(raw) || (folded && hay.includes(folded));
  });
  const specials = [];
  const regions = [];
  const cities = [];
  matched.forEach((item) => {
    if ((item.id || "") === "ru") specials.push(item);
    else if (isRegionGeo(item)) regions.push(item);
    else cities.push(item);
  });
  return specials.concat(regions, cities.slice(0, 80));
}

function visitUrl(job) {
  return job?.url || "";
}

function clampPctInput(raw) {
  if (raw === "") return "";
  const value = Number(raw);
  if (!Number.isFinite(value)) return "";
  return String(Math.min(100, Math.max(0, Math.trunc(value))));
}

function readPct(raw) {
  if (raw === "" || raw == null) return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 0 || value > 100) return null;
  return value;
}

function deviceMix(job) {
  const parts = [
    ["Windows", job?.win_pct],
    ["macOS", job?.mac_pct],
    ["iOS", job?.ios_pct],
    ["Android", job?.android_pct],
  ].filter(([, value]) => Number(value) > 0);
  if (!parts.length) return "";
  return parts.map(([name, value]) => `${name} ${value}%`).join(", ");
}

function behaviorLine(job) {
  const bits = [];
  if (Number(job?.bounce_max) > 0) {
    const used = Number(job.bounce_used);
    bits.push(
      used
        ? `отказ ${job.bounce_min}–${job.bounce_max}% (выпало ${used}%)`
        : `отказ ${job.bounce_min}–${job.bounce_max}%`
    );
  }
  if (Number(job?.pages_one) > 0) bits.push(`1 страница ${job.pages_one}%`);
  if (Number(job?.pages_two) > 0) bits.push(`до 2 страниц ${job.pages_two}%`);
  return bits.join(" · ");
}

function Pager({ page, pages, total, onPage, loading }) {
  if (pages <= 1) return null;
  return (
    <div className={loading ? "SiteVisits-pager is-loading" : "SiteVisits-pager"}>
      <button type="button" className="SiteVisits-fold" disabled={loading || page <= 1} onClick={() => onPage(page - 1)}>Назад</button>
      <span>{loading ? "Загрузка..." : `${page} из ${pages}${total ? `, всего ${total}` : ""}`}</span>
      <button type="button" className="SiteVisits-fold" disabled={loading || page >= pages} onClick={() => onPage(page + 1)}>Дальше</button>
    </div>
  );
}

function SiteVisits() {
  const { user, isLoading } = useAdminUser();
  const { enqueueSnackbar } = useSnackbar();
  const [options, setOptions] = useState(null);
  const [board, setBoard] = useState(null);
  const [url, setUrl] = useState("");
  const [visits, setVisits] = useState("40");
  const [geo, setGeo] = useState("kaliningrad");
  const [winPct, setWinPct] = useState("100");
  const [macPct, setMacPct] = useState("0");
  const [iosPct, setIosPct] = useState("0");
  const [androidPct, setAndroidPct] = useState("0");
  const [bounceMin, setBounceMin] = useState("10");
  const [bounceMax, setBounceMax] = useState("20");
  const [pagesOne, setPagesOne] = useState("40");
  const [pagesTwo, setPagesTwo] = useState("35");
  const [totalMin, setTotalMin] = useState("40");
  const [totalMax, setTotalMax] = useState("300");
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [folded, setFolded] = useState({ now: false, next: false, done: false });
  const queuePageRef = useRef(1);
  const donePageRef = useRef(1);
  const [queuePage, setQueuePage] = useState(1);
  const [donePage, setDonePage] = useState(1);
  const [paging, setPaging] = useState("");
  const [query, setQuery] = useState("");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const pagingToken = useRef(0);
  const queryRef = useRef("");
  const periodFromRef = useRef("");
  const periodToRef = useRef("");

  const allowed = ALLOWED_EMAILS.includes((user?.email || "").toLowerCase());

  const loadJobs = useCallback(async (section) => {
    const token = section ? pagingToken.current + 1 : 0;
    if (section) {
      pagingToken.current = token;
      setPaging(section);
    }
    const params = new URLSearchParams({
      queue_page: String(queuePageRef.current),
      done_page: String(donePageRef.current),
    });
    if (queryRef.current) params.set("q", queryRef.current);
    if (periodFromRef.current || periodToRef.current) {
      params.set("date_from", periodFromRef.current || periodToRef.current);
      params.set("date_to", periodToRef.current || periodFromRef.current);
    }
    try {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/site-visits/jobs?${params}`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (section && pagingToken.current !== token) return;
      if (!res.ok) {
        setLoadError(errorDetail(data, "Не удалось загрузить очередь"));
        return;
      }
      setLoadError("");
      setBoard(data);
      const nextQueue = data.queue_page || 1;
      const nextDone = data.done_page || 1;
      queuePageRef.current = nextQueue;
      donePageRef.current = nextDone;
      setQueuePage(nextQueue);
      setDonePage(nextDone);
    } finally {
      if (section && pagingToken.current === token) setPaging("");
    }
  }, []);

  const openQueuePage = (page) => {
    if (paging) return;
    queuePageRef.current = page;
    setQueuePage(page);
    loadJobs("queue");
  };

  const openDonePage = (page) => {
    if (paging) return;
    donePageRef.current = page;
    setDonePage(page);
    loadJobs("done");
  };

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    (async () => {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/site-visits/options`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (cancelled) return;
      if (!res.ok) {
        setLoadError(errorDetail(data, "Сервис визитов недоступен"));
        return;
      }
      setOptions(data);
      const geos = data.geos || [];
      setGeo((current) => (geos.some((item) => item.id === current) ? current : (geos[0]?.id || current)));
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  useEffect(() => {
    if (!allowed) return undefined;
    loadJobs();
    const timer = setInterval(loadJobs, 60000);
    return () => clearInterval(timer);
  }, [allowed, loadJobs]);

  const filtersReady = useRef(false);
  useEffect(() => {
    if (!allowed) return undefined;
    if (!filtersReady.current) {
      filtersReady.current = true;
      return undefined;
    }
    const handle = setTimeout(() => {
      queryRef.current = query.trim();
      periodFromRef.current = periodFrom;
      periodToRef.current = periodTo;
      queuePageRef.current = 1;
      donePageRef.current = 1;
      setQueuePage(1);
      setDonePage(1);
      loadJobs();
    }, 300);
    return () => clearTimeout(handle);
  }, [allowed, query, periodFrom, periodTo, loadJobs]);

  useEffect(() => {
    if (!board) return;
    const summary = board.summary || {};
    const filled = {
      now: (summary.jobs_running || board.running_jobs_total || 0) > 0,
      next: (summary.jobs_queued || board.queue_total || 0) > 0,
      done: (board.done_total || 0) > 0,
    };
    setFolded((prev) => ({
      now: filled.now ? false : prev.now,
      next: filled.next ? false : prev.next,
      done: filled.done ? false : prev.done,
    }));
  }, [board]);

  if (isLoading) return null;
  if (user && !allowed) return <Navigate to="/admin/clients" />;

  const submit = async (event) => {
    event.preventDefault();
    const count = Number(visits);
    const minSec = Number(totalMin);
    const maxSec = Number(totalMax);
    if (!url.trim()) {
      enqueueSnackbar("Укажите ссылку", { variant: "warning" });
      return;
    }
    if (!geo) {
      enqueueSnackbar("Выберите город", { variant: "warning" });
      return;
    }
    if (!Number.isFinite(count) || count < 1) {
      enqueueSnackbar("Укажите число визитов", { variant: "warning" });
      return;
    }
    if (!Number.isFinite(minSec) || !Number.isFinite(maxSec) || minSec > maxSec) {
      enqueueSnackbar("Проверьте длительность визита", { variant: "warning" });
      return;
    }
    const windows = readPct(winPct);
    const mac = readPct(macPct);
    const ios = readPct(iosPct);
    const android = readPct(androidPct);
    const bounceFrom = readPct(bounceMin);
    const bounceTo = readPct(bounceMax);
    const onePage = readPct(pagesOne);
    const twoPages = readPct(pagesTwo);
    if ([windows, mac, ios, android, bounceFrom, bounceTo, onePage, twoPages].some((value) => value == null)) {
      enqueueSnackbar("Проценты — целые числа от 0 до 100", { variant: "warning" });
      return;
    }
    if (windows + mac + ios + android !== 100) {
      enqueueSnackbar("Сумма Windows, macOS, iOS и Android должна быть 100%", { variant: "warning" });
      return;
    }
    if (bounceFrom > bounceTo) {
      enqueueSnackbar("Отказ «от» больше, чем «до»", { variant: "warning" });
      return;
    }
    if (bounceTo + onePage + twoPages > 100) {
      enqueueSnackbar("Отказ и страницы в сумме больше 100%", { variant: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/site-visits/jobs`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          url: url.trim(),
          utm_source: "",
          visits: count,
          geo,
          device: "windows",
          total_min: minSec,
          total_max: maxSec,
          win_pct: windows,
          mac_pct: mac,
          ios_pct: ios,
          android_pct: android,
          bounce_min: bounceFrom,
          bounce_max: bounceTo,
          pages_one: onePage,
          pages_two: twoPages,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        enqueueSnackbar(errorDetail(data, "Не удалось поставить задачу"), { variant: "error" });
        return;
      }
      enqueueSnackbar("Задача в очереди", { variant: "success" });
      setUrl("");
      queuePageRef.current = 9999;
      setQueuePage(9999);
      await loadJobs();
    } finally {
      setSubmitting(false);
    }
  };

  const retryToday = async () => {
    if (!window.confirm("Вернуть в очередь сегодняшние упавшие визиты, в том числе у ещё идущих задач? Старые дни не трогаются.")) return;
    setRetrying(true);
    try {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/site-visits/jobs/retry-today`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        enqueueSnackbar(errorDetail(data, "Не удалось поставить перезапуск в очередь"), { variant: "error" });
        return;
      }
      enqueueSnackbar("Перезапуск поставлен в очередь. Ошибки уходят порциями", { variant: "success" });
    } finally {
      setRetrying(false);
    }
  };

  const cancelJob = async (jobId) => {
    const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/site-visits/jobs/${jobId}/cancel`, {
      method: "POST",
      headers: authHeaders(),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      enqueueSnackbar(errorDetail(data, "Не удалось отменить"), { variant: "error" });
      return;
    }
    await loadJobs();
  };

  const geos = mergeGeoOptions((options?.geos || []).filter((item) => isRussianCity(item)));
  const devices = options?.devices || [];
  const summary = board?.summary || {};
  const concurrency = board?.concurrency || options?.concurrency || 30;
  const active = board?.active || null;
  const runningJobs = board?.running_jobs || [];
  const jobsRunning = summary.jobs_running ?? board?.running_jobs_total ?? runningJobs.length;
  const jobsQueued = summary.jobs_queued ?? board?.queue_total ?? 0;
  const visitsRunning = summary.visits_running ?? board?.running ?? 0;
  const visitsQueued = summary.visits_queued ?? board?.queued ?? 0;
  const visitsDone = summary.visits_done ?? 0;
  const visitsFailed = summary.visits_failed ?? 0;
  const visitsCancelled = summary.visits_cancelled ?? 0;
  const visitsTotal = visitsRunning + visitsQueued + visitsDone + visitsFailed + visitsCancelled;
  const queue = board?.queue || [];
  const history = board?.done || [];
  const queuePages = board?.queue_pages || 1;
  const donePages = board?.done_pages || 1;
  const queueTotal = board?.queue_total ?? jobsQueued;
  const doneTotal = board?.done_total || 0;
  const pageSize = board?.page_size || 10;
  const ssp = board?.ssp || {};
  const sspWorking = ssp.tasks_working || 0;
  const sspOpen = ssp.tasks_today_open || 0;
  const sspTodayDone = ssp.tasks_today_done || 0;
  const sspClicks = ssp.clicks_per_day || 0;
  const sspVisitsError = ssp.visits_error || 0;
  const todayFailed = Number(board?.today?.failed ?? sspVisitsError) || 0;
  const todayDone = Number(board?.today?.done) || 0;
  const geoLabelOf = (id) => {
    const item = geos.find((entry) => entry.id === id);
    return item ? cityTitle(item) : id;
  };
  const deviceLabelOf = (id) => devices.find((item) => item.id === id)?.label || id;
  const activeItems = active?.items || [];
  const deviceParts = [winPct, macPct, iosPct, androidPct].map(readPct);
  const deviceSum = deviceParts.every((value) => value != null)
    ? deviceParts.reduce((sum, value) => sum + value, 0)
    : NaN;
  const bounceFromPct = readPct(bounceMin);
  const bounceToPct = readPct(bounceMax);
  const onePagePct = readPct(pagesOne);
  const twoPagePct = readPct(pagesTwo);
  const behaviorOk = bounceFromPct != null
    && bounceToPct != null
    && onePagePct != null
    && twoPagePct != null
    && bounceFromPct <= bounceToPct
    && bounceToPct + onePagePct + twoPagePct <= 100;
  const fullFrom = behaviorOk ? 100 - bounceToPct - onePagePct - twoPagePct : 0;
  const fullTo = behaviorOk ? 100 - bounceFromPct - onePagePct - twoPagePct : 0;
  const metaOf = (job) => [deviceMix(job) || deviceLabelOf(job.device), behaviorLine(job)].filter(Boolean).join(" · ");
  const fmt = (value) => Number(value || 0).toLocaleString("ru-RU");
  const period = board?.period || null;

  return (
    <div className="SiteVisits">
      <div className="SiteVisits-header">
        <div className="SiteVisits-headerText">
          <h1>Настройка визитов</h1>
          <p>
            Одновременно идут до {concurrency} визитов из разных задач, в том числе из SSP-кликов.
            В очереди видны все поставленные задачи.
          </p>
        </div>
      </div>
      <section className="SiteVisits-board" aria-label="Сводка визитов">
        <div className="SiteVisits-metrics is-four">
          <div>
            <span>Сейчас идёт</span>
            <strong>{fmt(visitsRunning)}</strong>
            <small>из {concurrency} визитов</small>
          </div>
          <div>
            <span>В очереди</span>
            <strong>{fmt(visitsQueued)}</strong>
            <small>визитов ждут</small>
          </div>
          <div className={todayDone > 0 ? "is-ok" : undefined}>
            <span>Успешно за сегодня</span>
            <strong>{fmt(todayDone)}</strong>
            <small>визитов готово</small>
          </div>
          <div className={todayFailed > 0 ? "is-alert" : undefined}>
            <span>Ошибки за сегодня</span>
            <div className="SiteVisits-metricRow">
              <strong>{fmt(todayFailed)}</strong>
              <button
                type="button"
                className="SiteVisits-retry"
                disabled={retrying || todayFailed < 1}
                onClick={retryToday}
              >
                {retrying ? "Возвращаю…" : "Перезапустить"}
              </button>
            </div>
            <small>визитов, можно вернуть</small>
          </div>
        </div>
        <div className="SiteVisits-group">
          <h2>SSP клики</h2>
          <div className="SiteVisits-metrics">
            <div>
              <span>Кампаний в сетке</span>
              <strong>{fmt(sspWorking)}</strong>
              <small>{fmt(sspClicks)} кликов в день</small>
            </div>
            <div>
              <span>Сегодня не стартовали</span>
              <strong>{fmt(sspOpen)}</strong>
              <small>кампаний без запуска</small>
            </div>
            <div>
              <span>Сегодня уже закончились</span>
              <strong>{fmt(sspTodayDone)}</strong>
              <small>включая упавшие запуски</small>
            </div>
          </div>
        </div>
        {/* <div className="SiteVisits-group">
          <h2>Сервис за всё время</h2>
          <div className="SiteVisits-metrics is-four">
            <div>
              <span>Всего визитов</span>
              <strong>{fmt(visitsTotal)}</strong>
              <small>SSP и эта страница</small>
            </div>
            <div>
              <span>Прошло</span>
              <strong>{fmt(visitsDone)}</strong>
              <small>визитов</small>
            </div>
            <div>
              <span>Упало</span>
              <strong>{fmt(visitsFailed)}</strong>
              <small>архив</small>
            </div>
            <div>
              <span>Отменено</span>
              <strong>{fmt(visitsCancelled)}</strong>
              <small>визитов</small>
            </div>
          </div>
        </div> */}
      </section>

      <form className="SiteVisits-form" onSubmit={submit}>
        <label className="SiteVisits-wide">
          Ссылка
          <input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://site.ru/page?utm_source=..."
          />
        </label>
        <div className="SiteVisits-row">
          <label>
            Визитов
            <input
              type="number"
              min="1"
              max="500"
              value={visits}
              onChange={(event) => setVisits(event.target.value)}
            />
          </label>
          <label>
            Город
            <Autocomplete
              className="SiteVisits-city"
              options={geos}
              value={geos.find((item) => item.id === geo) || null}
              onChange={(_, next) => setGeo(next ? next.id : "")}
              getOptionLabel={(option) => cityTitle(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={filterCities}
              groupBy={geoGroup}
              openOnFocus
              noOptionsText="Ничего не найдено"
              ListboxProps={{ style: { maxHeight: 320 } }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Страна, область или город" />
              )}
            />
          </label>
        </div>
        <div className="SiteVisits-shares">
          <label>
            Windows, %
            <input type="number" min="0" max="100" value={winPct} onChange={(event) => setWinPct(clampPctInput(event.target.value))} />
          </label>
          <label>
            macOS, %
            <input type="number" min="0" max="100" value={macPct} onChange={(event) => setMacPct(clampPctInput(event.target.value))} />
          </label>
          <label>
            iOS, %
            <input type="number" min="0" max="100" value={iosPct} onChange={(event) => setIosPct(clampPctInput(event.target.value))} />
          </label>
          <label>
            Android, %
            <input type="number" min="0" max="100" value={androidPct} onChange={(event) => setAndroidPct(clampPctInput(event.target.value))} />
          </label>
        </div>
        <p className={deviceSum === 100 ? "SiteVisits-hint" : "SiteVisits-hint is-bad"}>
          {deviceSum === 100
            ? "Сумма устройств: 100%."
            : `Сумма устройств: ${Number.isFinite(deviceSum) ? deviceSum : "—"}%. Нужно 100%.`}
        </p>
        <div className="SiteVisits-shares">
          <label>
            Отказ от, %
            <input type="number" min="0" max="100" value={bounceMin} onChange={(event) => setBounceMin(clampPctInput(event.target.value))} />
          </label>
          <label>
            Отказ до, %
            <input type="number" min="0" max="100" value={bounceMax} onChange={(event) => setBounceMax(clampPctInput(event.target.value))} />
          </label>
          <label>
            Одна страница, %
            <input type="number" min="0" max="100" value={pagesOne} onChange={(event) => setPagesOne(clampPctInput(event.target.value))} />
          </label>
          <label>
            До 2 страниц, %
            <input type="number" min="0" max="100" value={pagesTwo} onChange={(event) => setPagesTwo(clampPctInput(event.target.value))} />
          </label>
        </div>
        <p className={behaviorOk ? "SiteVisits-hint" : "SiteVisits-hint is-bad"}>
          {behaviorOk
            ? `Отказ — короче 15 секунд и только страница из ссылки. Полный обход: ${fullFrom}–${fullTo}%.`
            : "Отказ «до», одна страница и до 2 страниц вместе не больше 100%, и «от» не больше «до»."}
        </p>
        <div className="SiteVisits-duration">
          <label>
            Секунд от
            <input
              type="number"
              min="10"
              value={totalMin}
              onChange={(event) => setTotalMin(event.target.value)}
            />
          </label>
          <label>
            Секунд до
            <input
              type="number"
              min="10"
              value={totalMax}
              onChange={(event) => setTotalMax(event.target.value)}
            />
          </label>
          <button className="SiteVisits-submit" type="submit" disabled={submitting || !options}>
            {submitting ? "Ставлю..." : "Поставить в очередь"}
          </button>
        </div>
      </form>

      <div className="SiteVisits-filters">
        <div className="SiteVisits-filterRow">
          <label className="SiteVisits-filterSearch">
            Поиск
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Кампания, сайт или кто поставил"
            />
          </label>
          <label>
            С
            <input type="date" value={periodFrom} onChange={(event) => setPeriodFrom(event.target.value)} />
          </label>
          <label>
            По
            <input type="date" value={periodTo} onChange={(event) => setPeriodTo(event.target.value)} />
          </label>
          {periodFrom || periodTo ? (
            <button
              type="button"
              className="SiteVisits-clear"
              onClick={() => {
                setPeriodFrom("");
                setPeriodTo("");
              }}
            >
              Сбросить даты
            </button>
          ) : null}
        </div>
        {period ? (
          <div className="SiteVisits-period">
            <div className="SiteVisits-periodNums">
              <div>
                <strong>{fmt(period.planned)}</strong>
                <span>Запланировано</span>
              </div>
              <div>
                <strong>{fmt(period.done)}</strong>
                <span>Сделано</span>
              </div>
              <div className={Number(period.failed) ? "is-alert" : ""}>
                <strong>{fmt(period.failed)}</strong>
                <span>С ошибкой</span>
              </div>
            </div>
            {(period.errors || []).length ? (
              <ol className="SiteVisits-errors">
                {(period.errors || []).map((item) => {
                  const count = Number(item.count) || 0;
                  const failed = Number(period.failed) || 0;
                  const pct = failed ? (100 * count) / failed : 0;
                  const digits = pct >= 10 ? 0 : 1;
                  const share = pct.toLocaleString("ru-RU", {
                    minimumFractionDigits: digits,
                    maximumFractionDigits: digits,
                  });
                  return (
                    <li key={item.reason}>
                      <b>{fmt(count)}</b>
                      <span title={item.reason}>{item.reason}</span>
                      <em>{share}%</em>
                      <span className="SiteVisits-errorBar" aria-hidden="true">
                        <span style={{ width: pct + "%" }} />
                      </span>
                    </li>
                  );
                })}
              </ol>
            ) : null}
          </div>
        ) : null}
      </div>

      {loadError ? <div className="SiteVisits-error">{loadError}</div> : null}

      <section className="SiteVisits-panel">
        <div className="SiteVisits-panelHead">
          <h2>Сейчас · {query.trim() ? runningJobs.length : jobsRunning}</h2>
          <button type="button" className="SiteVisits-fold" onClick={() => setFolded((prev) => ({ ...prev, now: !prev.now }))}>
            {folded.now ? "Показать" : "Скрыть"}
          </button>
        </div>
        {folded.now ? null : <div className="SiteVisits-panelBody">{runningJobs.length ? (
          <table className="SiteVisits-table">
            <thead>
              <tr>
                <th>Сайт</th>
                <th>Город</th>
                <th>Кто поставил</th>
                <th>Прошло</th>
                <th>Идёт</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {runningJobs.map((job, index) => (
                <tr key={job.id}>
                  <td className="SiteVisits-url" title={visitUrl(job)}>{index + 1}. {visitUrl(job)}</td>
                  <td className="SiteVisits-meta">{geoLabelOf(job.geo)}<span>{metaOf(job)}</span></td>
                  <td>{job.created_by || "—"}</td>
                  <td>{job.done} / {job.visits}</td>
                  <td>{job.running}</td>
                  <td>
                    <button type="button" className="SiteVisits-cancel" onClick={() => cancelJob(job.id)}>
                      Отменить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="SiteVisits-empty">Сейчас ничего не выполняется</p>
        )}
          {active && activeItems.length ? (
            <table className="SiteVisits-table SiteVisits-items">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Статус</th>
                  <th>IP</th>
                  <th>Экран</th>
                  <th>Ошибка</th>
                </tr>
              </thead>
              <tbody>
                {activeItems.filter((item) => item.status !== "queued").map((item) => (
                  <tr key={item.seq} className={item.status === "running" ? "is-running" : ""}>
                    <td>{item.seq}</td>
                    <td>{STATUS_LABEL[item.status] || item.status}</td>
                    <td>{item.ip || "—"}</td>
                    <td>{item.viewport || "—"}</td>
                    <td title={item.error || ""}>{item.error || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}</div>}
      </section>

      <section className="SiteVisits-panel">
        <div className="SiteVisits-panelHead">
          <h2>Очередь · {queueTotal}</h2>
          <button type="button" className="SiteVisits-fold" onClick={() => setFolded((prev) => ({ ...prev, next: !prev.next }))}>
            {folded.next ? "Показать" : "Скрыть"}
          </button>
        </div>
        {folded.next ? null : <div className={paging === "queue" ? "SiteVisits-panelBody is-loading" : "SiteVisits-panelBody"}>{queue.length === 0 ? (
          <p className="SiteVisits-empty">Следующих задач нет</p>
        ) : (
          <table className="SiteVisits-table">
            <thead>
              <tr>
                <th>Сайт</th>
                <th>Город</th>
                <th>Кто поставил</th>
                <th>Визитов</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {queue.map((job, index) => (
                <tr key={job.id}>
                  <td className="SiteVisits-url" title={visitUrl(job)}>{(queuePage - 1) * pageSize + index + 1}. {visitUrl(job)}</td>
                  <td className="SiteVisits-meta">{geoLabelOf(job.geo)}<span>{metaOf(job)}</span></td>
                  <td>{job.created_by || "—"}</td>
                  <td>{job.visits}</td>
                  <td>
                    <button type="button" className="SiteVisits-cancel" onClick={() => cancelJob(job.id)}>
                      Убрать
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}</div>}
        {folded.next ? null : <Pager page={queuePage} pages={queuePages} total={queueTotal} onPage={openQueuePage} loading={paging === "queue"} />}
      </section>

      <section className="SiteVisits-panel">
        <div className="SiteVisits-panelHead">
          <h2>Завершённые · {doneTotal}</h2>
          <button type="button" className="SiteVisits-fold" onClick={() => setFolded((prev) => ({ ...prev, done: !prev.done }))}>
            {folded.done ? "Показать" : "Скрыть"}
          </button>
        </div>
        {folded.done ? null : <div className={paging === "done" ? "SiteVisits-panelBody is-loading" : "SiteVisits-panelBody"}>
          <table className="SiteVisits-table">
            <thead>
              <tr>
                <th>Сайт</th>
                <th>Город</th>
                <th>Устройство</th>
                <th>Прошло</th>
                <th>Ошибки</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan="6" className="SiteVisits-empty">Завершённых задач нет</td>
                </tr>
              ) : history.map((job) => (
                <tr key={job.id}>
                  <td className="SiteVisits-url" title={visitUrl(job)}>{visitUrl(job)}</td>
                  <td>{geoLabelOf(job.geo)}</td>
                  <td className="SiteVisits-meta">{metaOf(job)}</td>
                  <td>{job.done} / {job.visits}</td>
                  <td title={job.last_error || ""}>{job.failed}</td>
                  <td>{STATUS_LABEL[job.status] || job.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
        {folded.done ? null : <Pager page={donePage} pages={donePages} total={doneTotal} onPage={openDonePage} loading={paging === "done"} />}
      </section>
    </div>
  );
}

export default SiteVisits;
